/**
 * Cycle 9 STORY-803: Industry vertical hub metadata.
 *
 * 5 industries with: name, label, description, key challenges, recommended product slugs,
 * and matching API filter (matches CatalogEntry.industry tags).
 */

export interface IndustryMetadata {
  slug: string;
  label: string;
  /** API filter value (matches CatalogEntry.industry array values) */
  apiFilter: string[];
  /** Hero subtitle */
  heroTitle: string;
  /** Hero description (~ 200 chars) */
  heroSubtitle: string;
  /** Description (300-500 chars) */
  description: string;
  /** Key business challenges in this industry (3-5 items) */
  challenges: { title: string; detail: string }[];
  /** Suggested product slugs (priority order) */
  recommendedProductSlugs: string[];
  /** Accent color */
  accentColor: string;
}

export const industries: IndustryMetadata[] = [
  {
    slug: "mining",
    label: "Горнодобыча и металлургия",
    apiFilter: ["горнодобыча", "металлургия", "обогащение"],
    heroTitle: "Цифровизация на ГОКах и обогатительных фабриках",
    heroSubtitle:
      "Безопасность смен, мониторинг гранулометрии в реальном времени, управление парком оборудования. Решения проверены в действующих горнодобывающих компаниях.",
    description:
      "Горнодобыча и металлургия — отрасли с высокими LTIFR-рисками, многоэтапными производственными циклами, и потребностью в реал-тайм-мониторинге технологических параметров. IT-Navigate объединяет HSE-обучение нового поколения (shift-brief, instruct-me), мониторинг качества (core-logic), и orchestration-инфраструктуру (crew-processing) в готовый стек.",
    challenges: [
      {
        title: "Высокий LTIFR + строгие требования Ростехнадзора",
        detail: "Решается shift-brief (геймифицированные инструктажи) + instruct-me (FaceID + автоотчётность).",
      },
      {
        title: "Потери качества концентрата при отклонениях гранулометрии",
        detail: "Решается core-logic — реал-тайм-мониторинг + ML-предсказания отклонений.",
      },
      {
        title: "Управление десятками агентов на удалённых площадках",
        detail: "Решается crew-processing — Kubernetes-lite для on-premise / edge.",
      },
    ],
    recommendedProductSlugs: ["shift-brief-safety", "instruct-me", "core-logic", "crew-processing"],
    accentColor: "#5f4a1a",
  },
  {
    slug: "production",
    label: "Производство и машиностроение",
    apiFilter: ["производство", "машиностроение", "энергетика"],
    heroTitle: "Промышленные предприятия и многозаводские структуры",
    heroSubtitle:
      "Стандартизация ОТ через 5+ предприятий, единая отчётность, автоматизация инструктажей. Опыт работы с холдингами 5000+ сотрудников.",
    description:
      "Машиностроение, химия, металлургия, энергетика — производственные предприятия требуют стандартизации обучения и отчётности по ОТ через несколько площадок одновременно. IT-Navigate Safety Suite даёт единый стек для холдингов с быстрым tenant-onboarding.",
    challenges: [
      {
        title: "Разные стандарты обучения на разных площадках",
        detail: "shift-brief + instruct-me работают через единый портал управления курсами.",
      },
      {
        title: "Подготовка отчётности 800+ требований Ростехнадзора занимает 40+ часов/мес",
        detail: "instruct-me автоматизирует ~95% рутины.",
      },
      {
        title: "Доступ к опасным зонам без проверки компетенций",
        detail: "shift-brief FaceID-верификация + biometric-связь с системой контроля доступа.",
      },
    ],
    recommendedProductSlugs: ["shift-brief-safety", "instruct-me", "green-safety"],
    accentColor: "#2c5f2d",
  },
  {
    slug: "retail",
    label: "Retail и FMCG",
    apiFilter: ["retail", "FMCG", "e-commerce"],
    heroTitle: "Логистика и прогнозы продаж в розничной торговле",
    heroSubtitle:
      "Оптимизация паллетирования распределительных центров, ML-прогнозы по тысячам SKU, anti-stockout аналитика. Опыт с X5 / Ашан / SPAR.",
    description:
      "Retail и FMCG — отрасли с высокой оборачиваемостью SKU, сезонностью, промо-активностями и логистическими цепочками. IT-Navigate Logistics Suite (smart-pallet + palletix + pallet-planner) и Sales Intelligence Suite (sales-planner + sales-analysis + sales-platinum) закрывают operational и planning слои одним вендором.",
    challenges: [
      {
        title: "Низкая загрузка паллет → дорогая логистика",
        detail: "smart-pallet + palletix оптимизируют укладку на 15-25%.",
      },
      {
        title: "Out-of-stock + overstock из-за неточных прогнозов",
        detail: "sales-planner снижает MAPE с 25-35% до 12-18%.",
      },
      {
        title: "Время планирования смены 4-6 часов",
        detail: "smart-pallet + pallet-planner — менее 30 минут.",
      },
    ],
    recommendedProductSlugs: ["smart-pallet", "palletix", "pallet-planner", "sales-planner", "sales-analysis"],
    accentColor: "#7a4f1a",
  },
  {
    slug: "energy",
    label: "Энергетика и нефтегаз",
    apiFilter: ["энергетика", "нефтегаз", "химия"],
    heroTitle: "Безопасность объектов критической инфраструктуры",
    heroSubtitle:
      "ОТ-обучение операторов опасных производственных объектов, отчётность Ростехнадзора, контроль доступа в зоны повышенной опасности.",
    description:
      "Энергетика, нефтегаз и большая химия — критически важные отрасли с строгими требованиями к компетенциям персонала и отчётности. IT-Navigate Safety Suite адаптирована под специфику ОПО: BIO-верификация, аудиторский след каждого инструктажа, интеграция с СКУД и DLP.",
    challenges: [
      {
        title: "Подделка прохождения инструктажей",
        detail: "FaceID-верификация даёт юридически валидное доказательство.",
      },
      {
        title: "Длительная подготовка отчётов Ростехнадзора",
        detail: "instruct-me автоматизирует выгрузку и формирование документов.",
      },
      {
        title: "Координация подрядных работ на распределённой инфраструктуре",
        detail: "crew-processing управляет десятками агентов на удалённых объектах.",
      },
    ],
    recommendedProductSlugs: ["shift-brief-safety", "instruct-me", "crew-processing"],
    accentColor: "#1a4a7a",
  },
  {
    slug: "agro-distribution",
    label: "Агропром и B2B-дистрибуция",
    apiFilter: ["агро", "FMCG", "B2B-дистрибуция"],
    heroTitle: "Sales Intelligence для дистрибьюторов и агропредприятий",
    heroSubtitle:
      "ML-прогнозы продаж по регионам и SKU, cross-sell рекомендации, анализ корзин. Опыт с дистрибьюторами 500+ клиентов.",
    description:
      "Агропромышленные комплексы и B2B-дистрибьюторы — отрасль с длинными SKU-каталогами, сезонностью, выраженной географической структурой клиентской базы. IT-Navigate Sales Intelligence Suite даёт точные прогнозы и upsell-рекомендации, интегрируется с Bitrix24 / AmoCRM.",
    challenges: [
      {
        title: "Низкая точность прогноза продаж по 1000+ SKU",
        detail: "sales-planner + sales-analysis: ML-стек из 6 моделей.",
      },
      {
        title: "Низкий средний чек по B2B-клиентам",
        detail: "sales-platinum: TiDE-модель + cross-sell на базе исторических корзин.",
      },
      {
        title: "Излишки склада в межсезонье",
        detail: "Прогнозы с учётом сезонности и промо снижают overstock на 28%.",
      },
    ],
    recommendedProductSlugs: ["sales-planner", "sales-analysis", "sales-platinum"],
    accentColor: "#5f2c5f",
  },
];

export function getIndustryBySlug(slug: string): IndustryMetadata | undefined {
  return industries.find((i) => i.slug === slug);
}
